import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useSearch } from 'wouter';

import { getComponentFromName } from '../../lib/utils';

import { usePage } from '../../contexts/PagesContext';
import { useLoading, useSite } from '../../contexts/SiteContext';
import { useFloatingVideo } from '../../contexts/VideoContext';
import * as Layouts from '../layouts/index';
import PageMeta from '../partials/PageMeta';
import * as Pages from './index';

const propTypes = {
    keepLoadedPage: PropTypes.bool,
    // withQuery: PropTypes.bool,
};

const defaultProps = {
    keepLoadedPage: true,
    // withQuery: false,
};

const PageLoader = ({ keepLoadedPage }) => {
    const [pathname = null] = useLocation();
    const search = useSearch();
    const { setFullscreen = null } = useFloatingVideo();
    const site = useSite();

    const { loading, error, page } = usePage({ pathname, search });
    const [lastAction, setLastAction] = useState(null);
    const [loadedPage, setLoadedPage] = useState(page);
    const currentSearch = useRef(search);
    const { loading: globalLoading, setLoading } = useLoading();

    const finalPage =
        keepLoadedPage && (loading || globalLoading) && page === null ? loadedPage : page;
    const { url: finalPagePath = '' } = finalPage || {};

    if (finalPage === page) {
        currentSearch.current = search;
    }

    const { pageType: type = null, site: pageSite = null } = finalPage || {};
    const { handle: siteHandle = null } = site || pageSite || {};
    let PageComponent = null;
    if (type !== null) {
        PageComponent = getComponentFromName(Pages, type, null);
    }

    const layoutType = useMemo(
        () => (siteHandle === 'employees' ? 'Employee' : 'main'),
        [siteHandle],
    );
    const LayoutComponent = getComponentFromName(Layouts, layoutType, Layouts.Main);

    useEffect(() => {
        if (keepLoadedPage && !loading && page !== null) {
            setFullscreen(false);
            setLoadedPage(page);
        }
    }, [keepLoadedPage, loading, page, setLoadedPage]);

    useEffect(() => {
        if (loading) {
            if (
                !(
                    finalPagePath.match(/news/) !== null ||
                    finalPagePath.match(/actualites/) !== null
                ) ||
                !(pathname.match(/news/) !== null || pathname.match(/actualites/) !== null)
            ) {
                setLoading(true);
            }
        }
    }, [loading, finalPage, finalPagePath, pathname]);

    // The scroll restoration magic
    // useEffect(() => {
    //     const unlisten = history.listen((newRoute, action) => {
    //         if (
    //             pathname !== null &&
    //             newRoute?.pathname !== null &&
    //             newRoute?.pathname !== pathname
    //             // action !== 'POP'
    //         ) {
    //             setLastAction(action);
    //         }
    //     });
    //     return unlisten;
    // }, [history, pathname, setLastAction]);

    useEffect(() => {
        if (finalPagePath !== null && lastAction !== null && lastAction !== 'POP') {
            window.scroll({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [lastAction, finalPagePath]);

    // Kill the loading on load
    useEffect(() => {
        if (page !== null && !loading) {
            setLoading(false);
        }
    }, [page, loading, setLoading]);

    // Fallback few seconds if page never loads
    useEffect(() => {
        let timeout;
        if (globalLoading) {
            timeout = setTimeout(() => {
                setLoading(false);
            }, 5000);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [globalLoading, setLoading]);

    return (
        <LayoutComponent site={site} page={!error ? finalPage : null} pageLoading={loading}>
            {PageComponent !== null && finalPage !== null ? (
                <>
                    <PageMeta page={finalPage} />
                    <PageComponent
                        site={site}
                        page={finalPage}
                        pathname={pathname}
                        queryString={currentSearch.current}
                    />
                </>
            ) : null}
        </LayoutComponent>
    );
};

PageLoader.propTypes = propTypes;
PageLoader.defaultProps = defaultProps;

export default PageLoader;
